import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Paragraph, Spacer, TextButton } from '@zillow/constellation';
import { yieldCallback } from '@zillow/yield-callback';
import PropTypes from 'prop-types';
import { FieldConfig } from './MortgageCalculator.utils.js';
import { commaSeparateNumber, getCommaSeparatedNumber } from '../../utils/zhl-calculator.js';
import TextField from '../Fields/TextField.js';
import CheckboxField from '../Fields/CheckboxField.js';
import SelectField from '../Fields/SelectField.js';
import useValidFieldUpdate from '../../utils/form/useValidFieldUpdate.js';
import DownPaymentField from './DownPaymentField.js';
import LegalPopover from '../CalculatorForm/LegalPopover.js';

function MortgageFields(_ref) {
  var className = _ref.className;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    advanced = _useState2[0],
    setAdvanced = _useState2[1];
  useSyncDownPaymentPercent();
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Home price",
    leftAdornment: "$",
    name: FieldConfig.homePrice.name,
    config: FieldConfig.homePrice.config,
    autoComplete: "off",
    format: commaSeparateNumber
  }), /*#__PURE__*/React.createElement(DownPaymentField, null), /*#__PURE__*/React.createElement(SelectField, {
    options: FieldConfig.loanProgram.options,
    label: "Loan program",
    name: FieldConfig.loanProgram.name,
    config: FieldConfig.loanProgram.config,
    autoComplete: "off"
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Interest rate",
    rightAdornment: "%",
    name: FieldConfig.interestRate.name,
    config: FieldConfig.interestRate.config,
    autoComplete: "off",
    tooltip: {
      title: 'Interest rate',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Representative interest rates are based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics.")
    }
  }), /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }, /*#__PURE__*/React.createElement(TextButton, {
    type: "button",
    onClick: yieldCallback(function () {
      return setAdvanced(!advanced);
    })
  }, advanced ? 'Simple' : 'Advanced')), advanced && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CheckboxField, {
    label: "Include PMI",
    name: FieldConfig.includePMI.name,
    tooltip: {
      title: 'Private mortgage insurance',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Mortgage insurance is required primarily for borrowers on a conventional loan with a down payment of less than 20% of the home's purchase price. It protects lenders against some or most of the losses that can occur when a borrower defaults on a mortgage loan. Contact your lender for more details.")
    }
  }), /*#__PURE__*/React.createElement(CheckboxField, {
    label: "Include taxes/insurance",
    name: FieldConfig.includeTaxesInsurance.name,
    tooltip: {
      title: 'Taxes and insurance',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Includes property taxes, homeowners insurance, private mortgage insurance, and homeowners association dues.")
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Property tax",
    rightAdornment: "%",
    name: FieldConfig.taxRate.name,
    config: FieldConfig.taxRate.config,
    autoComplete: "off",
    tooltip: {
      title: 'Property tax',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The mortgage payment calculator includes estimated property taxes. The value represents an annual tax on the homeowner's property and the tax amount is based on the home's value. To convert an annual property tax in dollars to a percentage, divide the property tax by your home value and then multiply by 100.")
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Home insurance",
    leftAdornment: "$",
    rightAdornment: "/year",
    name: FieldConfig.homeInsurance.name,
    config: FieldConfig.homeInsurance.config,
    format: commaSeparateNumber,
    autoComplete: "off",
    tooltip: {
      title: 'Home insurance',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Commonly known as hazard insurance, most lenders require insurance to provide damage protection for your home and personal property from a variety of events, including fire, lightning, burglary, vandalism, storms, explosions, and more. The personal liability coverage in standard homeowner's insurance policies is intended to protect against lawsuits involving injuries that occur on your property. Borrowers shop for their own home insurance.")
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "HOA dues",
    leftAdornment: "$",
    rightAdornment: "/mo",
    name: FieldConfig.hoa.name,
    config: FieldConfig.hoa.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Homeowners association dues',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Typically, owners of condominiums or townhomes are required to pay homeowners association dues (known as HOA fees), to cover common amenities or services within the property such as garbage collection, landscaping, snow removal, pool maintenance, and hazard insurance.")
    }
  })), /*#__PURE__*/React.createElement(LegalPopover, {
    triggerText: "Calculator disclaimer",
    text: "The mortgage calculator is intended to be used for educational purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and may not include other fees and costs that a lender may assess in addition to monthly principal and interest, such as taxes and insurance and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend."
  }));
}
MortgageFields.propTypes = process.env.NODE_ENV !== "production" ? {
  className: PropTypes.string
} : {};
MortgageFields.defaultProps = {
  className: undefined
};
function useSyncDownPaymentPercent() {
  var form = useFormContext();

  // Watch for changes and do side effects
  var lastValidFields = useRef(null);
  if (lastValidFields.current === null) {
    lastValidFields.current = form.getValues();
  }
  useValidFieldUpdate(form, function (name, value) {
    var _Object$assign;
    lastValidFields.current = Object.assign({}, lastValidFields.current, (_Object$assign = {}, _Object$assign[name] = value, _Object$assign));

    // 1) on change of homePrice, update downPayment based on downPaymentPercent
    if (name === FieldConfig.homePrice.name) {
      var nextDownPayment = lastValidFields.current[FieldConfig.downPaymentPercent.name] / 100 * lastValidFields.current[FieldConfig.homePrice.name];
      if (!isSameFloat(nextDownPayment, lastValidFields.current.downPayment)) {
        form.setValue(FieldConfig.downPayment.name, getCommaSeparatedNumber(nextDownPayment), {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    }

    // 2) on change of downPayment, update homePrice based on downPaymentPercent
    if (name === FieldConfig.downPayment.name) {
      var nextHomePrice = lastValidFields.current[FieldConfig.downPayment.name] / (lastValidFields.current[FieldConfig.downPaymentPercent.name] / 100);
      if (!isSameFloat(nextHomePrice, lastValidFields.current.homePrice)) {
        form.setValue(FieldConfig.homePrice.name, getCommaSeparatedNumber(nextHomePrice), {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    }

    // 3) on change of downPaymentPercent, update downPayment based on downPaymentPercent
    if (name === FieldConfig.downPaymentPercent.name) {
      var _nextDownPayment = lastValidFields.current[FieldConfig.downPaymentPercent.name] / 100 * lastValidFields.current[FieldConfig.homePrice.name];
      if (!isSameFloat(_nextDownPayment, lastValidFields.current.downPayment)) {
        form.setValue(FieldConfig.downPayment.name, getCommaSeparatedNumber(_nextDownPayment), {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    }
  });
}
function isSameFloat(float1, float2) {
  return Math.abs(Number(float1.toFixed(2)) - Number(float2.toFixed(2))) < Number.EPSILON;
}

export { MortgageFields as default };
